// =================================================================
// DOCUMENT SHARE FLAGS + PACKAGE SELECTORS
// =================================================================
//
// Three booleans on every loan document (`LoanDocumentMeta`) drive the
// third-party-review (TPR) / counterparty delivery workflow. They are
// plain metadata fields — edited through the SAME field-patch endpoint as
// every other document field (`POST /files/update_loan_doc_metadata/{dealId}`
// with `[{ _id, <flag>: true }]`), NOT a dedicated endpoint.
//
// Each flag has a matching *package selector*. Building a package zips
// every document carrying that flag, foldered by docGroup:
//
//   GET  /files/document_package/{dealId}/{selector}   → builds + downloads
//   POST /files/push_document_package/{dealId}         → builds + stages only
//
// Both also stage the zip as a tracked loan document (docType
// "Document Package - <Selector>", docGroup "Document Packages") and stamp
// each included doc with `shareStamp` { selector, packageOid, stagedAt,
// stagedBy, note } — which is what `getShareStamp` below reads for the
// "staged" badge.
//
// The operator flow the UI supports:
//   1. Toggle `includeInThirdPartyReview` on the docs to stage → build the
//      `third_party_review` package and send it to the reviewer.
//   2. On the reviewer's approval, toggle `approvedForThirdParty`.
//   3. Build the `tpr_approved` package — that's the approved send package.
//   `shareWithCounterparty` / `counterparty` is the independent counterparty
//   share track.
//
// Source of truth: eastview-os `frontend_documentation/document-share-packages.md`.

/**
 * Ordered flag registry. `field` is the document metadata key, `selector`
 * is the package-build selector it feeds. Order is the operator's
 * workflow order (stage → approve → counterparty) and is used verbatim
 * for the columns, the bulk popover, and the package menu.
 */
export const DOCUMENT_SHARE_FLAGS = [
  {
    field: "includeInThirdPartyReview",
    selector: "third_party_review",
    // Short header for the grid column (space is tight).
    columnLabel: "In TPR Package",
    // Full sentence-case label for forms + the bulk popover.
    label: "Include in third-party review",
    // Package menu entry.
    packageLabel: "Third-party review",
    // One-line purpose on the Packages popover card.
    packageDescription: "Files the outside reviewer needs to check.",
    description: "Stage this file in the third-party-review (TPR) package.",
  },
  {
    field: "approvedForThirdParty",
    selector: "tpr_approved",
    // NOTE: this column already shipped as "Approved For Third Party" —
    // keep the header stable so saved AG Grid column state still matches.
    columnLabel: "Approved For Third Party",
    label: "Approved for third party",
    packageLabel: "TPR approved",
    packageDescription: "Files the reviewer signed off on.",
    description:
      "TPR-approved — cleared for the third-party / investor send package.",
  },
  {
    field: "shareWithCounterparty",
    selector: "counterparty",
    columnLabel: "Share w/ Counterparty",
    label: "Share with counterparty",
    packageLabel: "Counterparty share",
    packageDescription: "Files shared with the counterparty on this loan.",
    description: "Include this file in the counterparty share package.",
  },
];

export const DOCUMENT_SHARE_FLAG_FIELDS = DOCUMENT_SHARE_FLAGS.map(
  (f) => f.field,
);

export const getShareFlagConfig = (field) =>
  DOCUMENT_SHARE_FLAGS.find((f) => f.field === field) || null;

export const getShareSelectorConfig = (selector) =>
  DOCUMENT_SHARE_FLAGS.find((f) => f.selector === selector) || null;

/**
 * Coerce a share flag to a real boolean. The backend defaults these to
 * `null` and documents created before the feature shipped omit them
 * entirely, so "missing" reads as false. Legacy rows can also carry the
 * Python-cased "True"/"False" strings from the old blob-metadata era.
 */
export const isShareFlagOn = (doc, field) => {
  const v = doc?.[field];
  if (typeof v === "boolean") return v;
  if (typeof v === "string") return v === "true" || v === "True";
  return false;
};

/** How many of `docs` carry `field` — drives the package-menu count chips. */
export const countDocsWithShareFlag = (docs, field) =>
  (docs || []).reduce((n, d) => (isShareFlagOn(d, field) ? n + 1 : n), 0);

/**
 * The read-only stamp written onto every document included in the most
 * recent package build. Returns null when the doc has never been packaged.
 */
export const getShareStamp = (doc) => doc?.shareStamp || null;

/**
 * What a package build will actually zip, mirroring the backend
 * (services/document_share.build_share_package): flag on, not hidden, has a
 * stored file, and not itself a built package. Note the backend does NOT
 * skip `excludeFromExport` here, so neither does this.
 */
export const isPackageDocType = (doc) =>
  String(doc?.docType || "").startsWith("Document Package");

export const getPackageContents = (docs, field) => {
  // Every flagged doc — including any that the build will skip, so the UI can
  // name them (and say why) instead of silently dropping them from the count.
  const members = (docs || []).filter((d) => isShareFlagOn(d, field));
  const isHidden = (d) =>
    d?.isHidden === true || d?.isHidden === "true" || d?.isHidden === "True";
  const send = members.filter(
    (d) => !isHidden(d) && !!d?.file_storage_name && !isPackageDocType(d),
  );
  const sendIds = new Set(send.map((d) => d._id));
  return { members, send, leftOut: members.filter((d) => !sendIds.has(d._id)) };
};

// The backend's docType label per selector — the staged zip is filed as
// "Document Package - <label>" (services/document_share._SELECTOR_LABELS).
const PACKAGE_DOC_TYPE_LABELS = {
  third_party_review: "Third Party Review",
  counterparty: "Counterparty",
  tpr_approved: "TPR Approved",
};

/** The most recently built (staged) zip for a selector, or null. */
export const getLatestPackageBuild = (docs, selector) => {
  const docType = `Document Package - ${PACKAGE_DOC_TYPE_LABELS[selector]}`;
  const ts = (d) =>
    new Date(d?.sharePackage?.builtAt || d?.uploadDate || 0).getTime() || 0;
  return (
    (docs || [])
      .filter((d) => d?.docType === docType)
      .sort((a, b) => ts(b) - ts(a))[0] || null
  );
};

/** Filename for a downloaded package zip. */
export const sharePackageFileName = (selector, loanIdOrName) =>
  `${loanIdOrName || "loan"} - ${
    getShareSelectorConfig(selector)?.packageLabel || selector
  }`;
